// ---------------------------------------------------------------------------
// render.js -- draws a World onto a canvas. Reads state, never mutates it.
// ---------------------------------------------------------------------------

// Shown in the bottom-right corner of the scene so a screenshot from a tester
// says which build it came from. Bump on every push to the live theme.
//   1.0.0  first Shopify release (section + block)
//   1.1.0  compact composition for narrow containers
//   1.2.0  firmware limits on the release point; typed fields capped at the
//          displayed precision
//   1.3.0  sense-zone widening capped to the rapid trigger band; presets grow
//          their stroke to clear the zone (actuation 0.7 / release 0.8 read as
//          "always on")
//   1.4.0  follows the storefront language (English, Japanese)
const VERSION = '1.4.0';

// Strings for the current language, see i18n.js. Every entry point takes a
// `T` from RTI18N.pick() so two instances on one page can differ; the default
// is English.
const defaultStrings = () => window.RTI18N.pick(window.RTI18N.DEFAULT_LOCALE);

const C = {
  bg:        '#0e1015',
  barEmpty:  '#2a3040',
  barStroke: '#3a4356',
  onTop:     '#f0b429',
  onBottom:  '#ffdd57',
  offTop:    '#3f81c9',
  offBottom: '#79b6ea',
  text:      '#e6e9ef',
  muted:     '#98a1b3',
  line:      '#c9d1e0',
  plate:     '#1b202b',
  zone:      'rgba(122, 162, 247, 0.32)',
  zoneText:  '#9dbcf5',
  senseStroke: '#7ee060',
  senseFill:   'rgba(126, 224, 96, 0.22)',
  senseText:   '#a6ef88',
  grad:        '#ffffff',
  gradText:    '#8b94a6',
  version:     '#5d6678',
};

// Two layouts for the same scene. WIDE is the desktop composition, and is
// also what the GIF export uses. COMPACT is for narrow containers (phones):
// the same elements, tightened so that at ~520 CSS px the canvas renders near
// 1:1 and its text stays legible instead of being scaled to a third of its
// size. Font sizes live here rather than in the drawing code so the two can
// differ.
const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';
const SANS = 'ui-sans-serif, "Segoe UI", system-ui, sans-serif';

const LAYOUT = {
  w: 960, h: 720,   // 4:3
  barW: 96,
  barRadius: 28,  // corner radius; < barW/2 leaves a flat run at each end
  barMaxH: 285,   // pixel height of the deeper of the two switches
  barTop: 112,
  columns: [320, 640],   // bar centres, symmetric about the canvas centre
  minorMM: 0.25,  // graduation spacing; every other one is a labelled major
  gradTick: [20, 11],    // major / minor tick length
  gradLabelX: 22,        // graduation labels, right-aligned this far left of the bar
  legendX: 44, legendY: 79,   // legend, centred between the control strip and the bars
  // Footer stack, as offsets below the bottom of the tallest bar. countDY is
  // set so the counter sits centred in the gap between the name plate and the
  // parameter card below it, with room for its glow either side.
  pillDY: 34, plateDY: 58, plateH: 44, countDY: 135,
  // Right-of-bar furniture, as offsets from the bar's right edge.
  rtBandX: 6, rtBandW: 18,   // the RT zone strip and its rotated label
  ruleEnd: 30,               // where the threshold rules stop
  plateX: 36, platePad: 16,  // ACTUATION / RELEASE plates
  sensePlateX: 130,          // the SENSE ZONE plate ...
  senseLabel: 'plate',       // ... drawn out on a leader
  gradLabels: 'both',
  font: { grad: 11, label: 12, legend: 12, pill: 15, name: 22, count: 13, band: 10, sense: 12 },
};

const COMPACT = {
  w: 480, h: 440,
  barW: 68,
  barRadius: 20,
  barMaxH: 240,
  barTop: 84,
  columns: [133, 323],
  minorMM: 0.25,
  gradTick: [15, 8],
  gradLabelX: 17,
  // Both bars share one mm-per-pixel scale, so their ticks line up across the
  // canvas and the analog bar's labels would only repeat the regular's. In the
  // compact layout that width is worth more than the repetition.
  gradLabels: 'first',
  legendX: 16, legendY: 40,
  pillDY: 26, plateDY: 46, plateH: 32, countDY: 96,
  rtBandX: 6, rtBandW: 14,
  ruleEnd: 24,
  plateX: 28, platePad: 8,
  sensePlateX: 0,
  senseLabel: 'inline',      // there is no room for a leader; label the box itself
  font: { grad: 11, label: 11.5, legend: 11.5, pill: 13, name: 18, count: 12.5, band: 8.5, sense: 9.5 },
};

const LAYOUTS = { wide: LAYOUT, compact: COMPACT };

// Blend two hex colours, for the press flash.
function mix(a, b, t) {
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ch = sh => Math.round(((pa >> sh) & 255) * (1 - t) + ((pb >> sh) & 255) * t);
  return `rgb(${ch(16)},${ch(8)},${ch(0)})`;
}

// Shared mm-to-pixel scale. Both bars use it, so if one switch is configured
// with less total travel its bar is genuinely shorter and depths stay
// comparable across the two.
function geometry(world, layout = LAYOUT) {
  return {
    ...layout,
    scale: layout.barMaxH / world.maxTravel,
    yOf(mm) { return layout.barTop + mm * this.scale; },
    mmOf(y) { return (y - layout.barTop) / this.scale; },
  };
}

// Bar outline. Rounded corners, but a flat run across the top and bottom --
// a full half-circle cap reads as a lozenge and makes the fill level hard to
// judge near the ends.
function barShape(ctx, x, y, w, h, radius) {
  roundRect(ctx, x, y, w, h, Math.min(radius, w / 2, h / 2));
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function pill(ctx, cx, cy, text, color, filled, size) {
  ctx.font = `600 ${size}px ${MONO}`;
  const w = ctx.measureText(text).width + size * 1.7;
  const h = size * 1.75;
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 7);
  if (filled) { ctx.fillStyle = 'rgba(255, 209, 61, 0.14)'; ctx.fill(); }
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy + 1);
}

// Graduations laid over the bar itself: a faint dash every `minorMM`, with
// every other one -- the .0 and .5 marks -- drawn longer, brighter, and
// labelled just outside the bar. Each bar graduates its own travel, so two
// switches with different travels each get a correct ruler.
function drawGraduations(ctx, geo, x, barH, travel, withLabels) {
  const step = geo.minorMM;
  const isMajor = mm => Math.abs(mm / (step * 2) - Math.round(mm / (step * 2))) < 1e-6;

  ctx.save();
  barShape(ctx, x, geo.barTop, geo.barW, barH, geo.barRadius);
  ctx.clip();
  for (let i = 0; i * step <= travel + 1e-9; i++) {
    const mm = i * step;
    const y = geo.yOf(mm);
    const major = isMajor(mm);
    ctx.strokeStyle = C.grad;
    ctx.globalAlpha = major ? 0.58 : 0.26;
    ctx.lineWidth = major ? 1.6 : 1;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + (major ? geo.gradTick[0] : geo.gradTick[1]), y);
    ctx.stroke();
  }
  ctx.restore();

  if (!withLabels) return;
  ctx.font = `500 ${geo.font.grad}px ${MONO}`;
  ctx.fillStyle = C.gradText;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  for (let i = 0; i * step <= travel + 1e-9; i++) {
    const mm = i * step;
    if (!isMajor(mm)) continue;
    ctx.fillText(mm.toFixed(1), x - geo.gradLabelX, geo.yOf(mm));
  }
}

const legend = T => [[C.onBottom, T.canvas.on], [C.offBottom, T.canvas.off],
                     [C.senseStroke, T.canvas.senseZone]];

function legendWidth(ctx, geo, T) {
  ctx.font = `600 ${geo.font.legend}px ${MONO}`;
  let w = 0;
  for (const [, label] of legend(T)) w += 19 + ctx.measureText(label).width + 24;
  return w - 24;
}

function drawLegend(ctx, geo, T) {
  let x = geo.legendX;
  const y = geo.legendY;
  ctx.font = `600 ${geo.font.legend}px ${MONO}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  for (const [col, label] of legend(T)) {
    roundRect(ctx, x, y - 6, 12, 12, 3);
    ctx.fillStyle = col;
    ctx.fill();
    ctx.fillStyle = C.muted;
    ctx.fillText(label, x + 19, y + 1);
    x += 19 + ctx.measureText(label).width + 24;
  }
}

// Bounding box of everything the renderer actually draws, in logical
// coordinates. The on-screen layout deliberately leaves dead space at the top
// and bottom of the canvas for the HTML control overlays; those are not part of
// the drawing, so a GIF of the full canvas comes out badly framed. The export
// uses this to crop to the artwork instead.
function contentBounds(ctx, world, T = defaultStrings()) {
  const geo = geometry(world, LAYOUT);
  const footY = geo.barTop + geo.barMaxH;

  let minX = geo.legendX;
  let maxX = geo.legendX + legendWidth(ctx, geo, T);

  // Measure the plates rather than guessing their width -- an over-estimate
  // here shows up directly as a lopsided margin in the exported frame.
  ctx.font = `600 ${geo.font.label}px ${MONO}`;
  const plateW = t => ctx.measureText(t).width + geo.platePad;
  const thresholdW = Math.max(plateW(T.canvas.actuation), plateW(T.canvas.release));

  for (let i = 0; i < geo.columns.length; i++) {
    const x = geo.columns[i] - geo.barW / 2;
    minX = Math.min(minX, x - 44);                                 // graduation labels
    maxX = Math.max(maxX, x + geo.barW + geo.plateX + thresholdW);
  }

  // The analog column's SENSE ZONE plate reaches furthest right. Mirror the
  // renderer's clamp exactly, including its fixed 104 clamp width.
  const ax = geo.columns[1] - geo.barW / 2;
  maxX = Math.max(maxX, Math.min(ax + geo.barW + geo.sensePlateX,
                                 geo.w - 12 - 104) + plateW(T.canvas.senseZone));

  // Name plates are the widest thing in the footer.
  ctx.font = `700 ${geo.font.name}px ${SANS}`;
  [T.canvas.regularSwitch, T.canvas.analogSwitch].forEach((t, i) => {
    const tw = ctx.measureText(t).width + 44;
    minX = Math.min(minX, geo.columns[i] - tw / 2);
    maxX = Math.max(maxX, geo.columns[i] + tw / 2);
  });

  const top = geo.legendY - 11;
  const bottom = footY + geo.countDY + 24;   // counter text plus its glow
  return { x: minX, y: top, w: maxX - minX, h: bottom - top };
}

// One switch column: zone strip, bar, fill, graduations, markers, state pill,
// name plate.
function drawSwitch(ctx, geo, cx, sw, title, T) {
  const { barW, barTop } = geo;
  const x = cx - barW / 2;
  const barH = sw.travel * geo.scale;
  const yOf = mm => barTop + mm * geo.scale;

  // Rapid trigger band, as a labelled strip on the right of the bar. Its ends
  // coincide with the ACTUATION and RELEASE rules, so those trace its extent
  // rather than cutting across the label.
  for (const z of sw.zones) {
    const y0 = yOf(z.from), y1 = yOf(z.to);
    const h = Math.max(0, y1 - y0);
    const bx = x + barW + geo.rtBandX;
    roundRect(ctx, bx, y0, geo.rtBandW, h, 5);
    ctx.fillStyle = C.zone;
    ctx.fill();

    if (h > geo.font.band * 7.5) {   // only label it when the band is tall enough to read
      ctx.save();
      ctx.translate(bx + geo.rtBandW / 2, (y0 + y1) / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.font = `600 ${geo.font.band}px ${MONO}`;
      ctx.fillStyle = C.zoneText;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(T.canvas.rtZone, 0, 0.5);
      ctx.restore();
    }
  }

  // Empty channel.
  barShape(ctx, x, barTop, barW, barH, geo.barRadius);
  ctx.fillStyle = C.barEmpty;
  ctx.fill();
  ctx.strokeStyle = C.barStroke;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Fill from the top down. Clipped to the bar outline so the rounded corners
  // survive at any level. `sw.depth` is already clamped to this switch's travel.
  const fillH = sw.depth * geo.scale;
  if (fillH > 0.5) {
    ctx.save();
    barShape(ctx, x, barTop, barW, barH, geo.barRadius);
    ctx.clip();
    const g = ctx.createLinearGradient(0, barTop, 0, barTop + fillH);
    g.addColorStop(0, sw.on ? C.onTop : C.offTop);
    g.addColorStop(1, sw.on ? C.onBottom : C.offBottom);
    ctx.fillStyle = g;
    ctx.fillRect(x, barTop, barW, fillH);
    ctx.restore();
  }

  drawGraduations(ctx, geo, x, barH, sw.travel,
                  geo.gradLabels === 'both' || sw.column === 0);

  // Static threshold markers: rule across the bar, label plate to the right.
  // Labels are pushed apart when two thresholds sit close together (easy to do
  // now that the values can be typed in), with a connector back to the true
  // line position so the plate never lies about where the threshold is.
  ctx.font = `600 ${geo.font.label}px ${MONO}`;
  const PLATE_H = geo.font.label * 1.85, PLATE_GAP = PLATE_H + 4;
  let prevLabelY = -Infinity;
  for (const m of [...sw.markers].sort((a, b) => a.depth - b.depth)) {
    const y = yOf(m.depth);
    const labelY = Math.max(y, prevLabelY + PLATE_GAP);
    prevLabelY = labelY;

    ctx.strokeStyle = C.line;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + barW + geo.ruleEnd, y);
    ctx.stroke();

    if (labelY !== y) {
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + barW + geo.ruleEnd, y);
      ctx.lineTo(x + barW + geo.plateX, labelY);
      ctx.stroke();
    }

    const label = T.canvas[m.key];
    const lw = ctx.measureText(label).width + geo.platePad;
    roundRect(ctx, x + barW + geo.plateX, labelY - PLATE_H / 2, lw, PLATE_H, 6);
    ctx.fillStyle = C.plate;
    ctx.fill();
    ctx.strokeStyle = C.line;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = C.muted;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + barW + geo.plateX + geo.platePad / 2, labelY + 1);
  }

  // The sense zone -- the dynamic dead zone trailing the key's travel. Drawn
  // last so it sits over the graduations and threshold rules, with its own
  // leader out to a plate placed beyond the other plates to stay clear.
  //
  // Drawn at exactly the size the switch is using -- `AnalogSwitch` applies the
  // display widening itself, so the box on screen is literally the window that
  // decides the next toggle rather than a decorative stand-in for it.
  if (sw.sense) {
    const cyMM = (sw.sense.from + sw.sense.to) / 2;
    const h = (sw.sense.to - sw.sense.from) * geo.scale;
    // Exaggerated, so near either end of the travel it would otherwise hang
    // off the bar. Keep the drawn box inside the channel.
    const midY = yOf(cyMM);
    const y0 = Math.max(barTop, Math.min(barTop + barH - h, midY - h / 2));

    roundRect(ctx, x + 3, y0, barW - 6, h, Math.min(8, h / 2));
    ctx.fillStyle = C.senseFill;
    ctx.fill();
    ctx.strokeStyle = C.senseStroke;
    ctx.lineWidth = 2;
    ctx.stroke();

    if (geo.senseLabel === 'inline') {
      // Compact: no room for a leader, so the box carries its own label.
      ctx.font = `700 ${geo.font.sense}px ${MONO}`;
      ctx.fillStyle = C.senseText;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(T.canvas.senseZone, x + barW / 2, y0 + h / 2 + 0.5);
    } else {
      // Never let the plate run off the canvas, whatever the column position.
      const plateW = 104;
      const plateX = Math.min(x + barW + geo.sensePlateX, geo.w - 12 - plateW);
      const plateY = Math.max(barTop + 12,
                     Math.min(barTop + geo.barMaxH - 12, midY + 40));

      ctx.beginPath();
      ctx.moveTo(x + barW - 3, midY);
      ctx.lineTo(plateX - 8, plateY);
      ctx.strokeStyle = C.senseStroke;
      ctx.lineWidth = 1.25;
      ctx.stroke();

      ctx.font = `600 ${geo.font.sense}px ${MONO}`;
      const label = T.canvas.senseZone;
      const lw = ctx.measureText(label).width + 16;
      roundRect(ctx, plateX, plateY - 11, lw, 22, 6);
      ctx.fillStyle = C.plate;
      ctx.fill();
      ctx.strokeStyle = C.senseStroke;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = C.senseText;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, plateX + 8, plateY + 1);
    }
  }

  const footY = barTop + geo.barMaxH;

  pill(ctx, cx, footY + geo.pillDY, sw.on ? T.canvas.on : T.canvas.off,
       sw.on ? C.onBottom : C.muted, sw.on, geo.font.pill);

  ctx.font = `700 ${geo.font.name}px ${SANS}`;
  const tw = ctx.measureText(title).width + geo.font.name * 2;
  roundRect(ctx, cx - tw / 2, footY + geo.plateDY, tw, geo.plateH, 8);
  ctx.fillStyle = C.plate;
  ctx.fill();
  ctx.fillStyle = C.text;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(title, cx, footY + geo.plateDY + geo.plateH / 2 + 1);

  // Press counter -- the actual payoff of the comparison. On each new press the
  // text flashes green and a halo blooms behind it, which makes a fast run of
  // actuations readable even when the number itself is climbing too quickly to
  // follow. The halo fades twice as fast as the text.
  ctx.font = `500 ${geo.font.count}px ${MONO}`;
  const count = window.RTI18N.fmt(
    sw.presses === 1 && T.canvas.countOne ? T.canvas.countOne : T.canvas.count,
    { n: sw.presses, mm: sw.depth.toFixed(2) });
  const ty = footY + geo.countDY;

  if (sw.glow > 0) {
    // The glow is the text's own shadow, so it is a soft Gaussian bloom shaped
    // to the glyphs rather than a box behind them. Three passes at decreasing
    // radius build a bright core inside a wide halo; the blurred area is only
    // the size of one short line of text, so the filter cost stays small.
    const ease = sw.glow * sw.glow;   // fade out fast, no lingering haze
    ctx.save();
    ctx.fillStyle = `rgba(126, 224, 96, ${0.55 * ease})`;
    for (const radius of [20, 11, 6]) {
      ctx.shadowColor = `rgba(126, 224, 96, ${0.9 * ease})`;
      ctx.shadowBlur = radius * (0.45 + 0.55 * ease);
      ctx.fillText(count, cx, ty);
    }
    ctx.restore();
  }

  ctx.fillStyle = sw.flash > 0 ? mix(C.muted, C.senseStroke, sw.flash) : C.muted;
  ctx.fillText(count, cx, ty);
}

// How green the press counter still is, 1 at the moment of a press decaying to
// 0. Driven by the switch's own millisecond counter, so it is identical in the
// live view and in an exported GIF.
const FLASH_TEXT_MS = 640;   // press counter colour fading back to grey
const FLASH_GLOW_MS = 320;   // backdrop halo -- twice as fast as the text
const flashOf = sw => Math.max(0, 1 - sw.msSincePress / FLASH_TEXT_MS);
const glowOf  = sw => Math.max(0, 1 - sw.msSincePress / FLASH_GLOW_MS);

function render(ctx, world, layout = LAYOUT, T = defaultStrings()) {
  const geo = geometry(world, layout);
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, geo.w, geo.h);

  drawSwitch(ctx, geo, geo.columns[0], {
    depth: world.regular.depth,
    travel: world.regular.params.travel,
    on: world.regular.on,
    markers: world.regular.markers(),
    zones: world.regular.zones(),
    sense: null,
    presses: world.regular.presses,
    column: 0,
    flash: flashOf(world.regular),
    glow: glowOf(world.regular),
  }, T.canvas.regularSwitch, T);

  drawSwitch(ctx, geo, geo.columns[1], {
    depth: world.analog.depth,
    travel: world.analog.params.travel,
    on: world.analog.on,
    markers: world.analog.markers(),
    zones: world.analog.zones(),
    sense: world.analog.senseZone(),
    presses: world.analog.presses,
    column: 1,
    flash: flashOf(world.analog),
    glow: glowOf(world.analog),
  }, T.canvas.analogSwitch, T);

  drawLegend(ctx, geo, T);
  drawVersion(ctx, geo);
}

// Bottom-right corner, outside everything contentBounds() measures, so it is
// on screen but not in the exported GIF.
function drawVersion(ctx, geo) {
  ctx.font = `500 ${geo.font.grad}px ${MONO}`;
  ctx.fillStyle = C.version;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(`v${VERSION}`, geo.w - 10, geo.h - 9);
}

// Which bar, if any, sits under a point in logical canvas space. Manual drag
// uses this so it only starts on a bar rather than anywhere in the scene.
function barAt(world, px, py, layout = LAYOUT) {
  const geo = geometry(world, layout);
  const travels = [world.regular.params.travel, world.analog.params.travel];
  for (let i = 0; i < geo.columns.length; i++) {
    const x = geo.columns[i] - geo.barW / 2;
    const h = travels[i] * geo.scale;
    if (px >= x && px <= x + geo.barW &&
        py >= geo.barTop && py <= geo.barTop + h) return i;
  }
  return -1;
}

window.RTRender = { render, geometry, barAt, contentBounds, LAYOUT, LAYOUTS, C, VERSION };
